import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/features/auth/AuthContext";
import { useToast } from "@/ui/primitives/use-toast";
import {
  LGoButton,
  LGoCard,
  LGoCardHeader,
  LGoCardTitle,
  LGoCardDescription,
  LGoCardContent,
  LGoFormField,
} from "@/ui/branded";
import { authRepo } from "@/data/repos/authRepo";
import { resetPasswordSchema, type ResetPasswordInput } from "@/domain/user/schema";

export default function ResetPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  useEffect(() => {
    if (loading) return;
    if (user) {
      setSessionReady(true);
      return;
    }
    toast({
      variant: "destructive",
      title: "Neplatný odkaz",
      description: "Odkaz pro obnovu hesla vypršel nebo je neplatný. Požádejte o nový.",
    });
    navigate("/auth");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  const onSubmit = async (data: ResetPasswordInput) => {
    setIsLoading(true);
    const { error } = await authRepo.updatePassword(data.password);
    setIsLoading(false);

    if (error) {
      toast({ variant: "destructive", title: "Chyba", description: error.message });
      return;
    }
    toast({ title: "Heslo změněno", description: "Vaše heslo bylo úspěšně změněno." });
    navigate("/");
  };

  if (loading || !sessionReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-1">
          <h1 className="font-display text-6xl font-semibold tracking-[-0.05em] text-foreground">
            LastGO<span className="text-primary">.</span>
          </h1>
          <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground">
            Partnerský portál
          </p>
        </div>

        <LGoCard variant="raised" padding="lg">
          <LGoCardHeader>
            <LGoCardTitle className="text-2xl">Nové heslo</LGoCardTitle>
            <LGoCardDescription>Zadejte své nové heslo</LGoCardDescription>
          </LGoCardHeader>
          <LGoCardContent>
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <LGoFormField
                  name="password"
                  label="Nové heslo"
                  type="password"
                  placeholder="••••••••"
                />
                <LGoFormField
                  name="confirmPassword"
                  label="Potvrzení hesla"
                  type="password"
                  placeholder="••••••••"
                />
                <LGoButton type="submit" variant="primary" loading={isLoading} className="w-full">
                  Nastavit nové heslo
                </LGoButton>
              </form>
            </FormProvider>
          </LGoCardContent>
        </LGoCard>
      </div>
    </div>
  );
}
